import React, { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Typography, Box, Divider, Alert, CircularProgress
} from '@mui/material';
import {
  FileUpload as ImportIcon,
  FileDownload as ExportIcon,
  FolderOpen as FolderIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = window.electronAPI?.isElectron
  ? 'http://localhost:34512/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

/**
 * DesktopImportExport — bouton flottant Import / Export .eraproject
 * Utilisé uniquement dans Electron (mode desktop).
 */
export default function DesktopImportExport({ projects = [], onImported }) {
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = async (project) => {
    setLoading(true);
    setMessage(null);
    try {
      // Ask user where to save
      const defaultName = `${project.title.replace(/[^a-z0-9]/gi, '_')}.eraproject`;
      const savePath = await window.electronAPI.saveEraProjectDialog(defaultName);
      if (!savePath) { setLoading(false); return; }

      // Fetch export data from backend
      const resp = await axios.get(`${API_BASE}/desktop/export/${project.id}`, {
        responseType: 'text',
        headers: { Authorization: `Bearer ${localStorage.getItem('era_token')}` }
      });

      // Write to disk via IPC
      const encoder = new TextEncoder();
      const ok = await window.electronAPI.writeFile(savePath, encoder.encode(resp.data));

      if (ok) {
        setMessage({ type: 'success', text: `Projet exporté : ${savePath}` });
      } else {
        setMessage({ type: 'error', text: 'Impossible d\'écrire le fichier.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    setLoading(true);
    setMessage(null);
    try {
      // Ask user to pick a .eraproject file
      const filePath = await window.electronAPI.openEraProjectDialog();
      if (!filePath) { setLoading(false); return; }

      // Read file content
      const raw = await window.electronAPI.readFile(filePath);
      if (!raw) throw new Error('Impossible de lire le fichier sélectionné.');

      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(new Uint8Array(raw));
      const data = JSON.parse(text);

      // Send to backend
      const resp = await axios.post(`${API_BASE}/desktop/import`, data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('era_token')}`
        }
      });

      setMessage({ type: 'success', text: `Projet importé avec succès (ID: ${resp.data.projectId})` });
      if (onImported) onImported();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!window.electronAPI?.isElectron) return null;

  return (
    <>
      {/* Boutons déclencheurs */}
      <Button
        variant="outlined"
        startIcon={<ImportIcon />}
        onClick={handleImport}
        disabled={loading}
        sx={{ mr: 1 }}
      >
        Importer .eraproject
      </Button>
      <Button
        variant="outlined"
        startIcon={<ExportIcon />}
        onClick={() => setOpen(true)}
        disabled={loading || projects.length === 0}
      >
        Exporter un projet
      </Button>

      {/* Dialog de sélection du projet à exporter */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Exporter un projet
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sélectionnez le projet à exporter au format <strong>.eraproject</strong>.
            Ce fichier pourra être réimporté dans ERA Project Desktop ou Web.
          </Typography>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}
          <List>
            {projects.map((p, i) => (
              <React.Fragment key={p.id}>
                {i > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={p.title}
                    secondary={p.description?.slice(0, 80) || 'Aucune description'}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleExport(p)}
                      disabled={loading}
                      title="Exporter ce projet"
                    >
                      {loading ? <CircularProgress size={20} /> : <FolderIcon />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback hors dialog (import) */}
      {!open && message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mt: 2 }}
        >
          {message.text}
        </Alert>
      )}
    </>
  );
}
